import { QRCodeCanvas } from 'qrcode.react'
import { CreditCard, Loader2, Smartphone, Tag, X } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'react-toastify'
import {
  getPaymentStatus,
  payInstantSession,
  validateCoupon
} from '@cannect/services/resources/instantConsultation/requests'
import { TSessionPaymentPayload } from '@cannect/services/resources/instantConsultation/types'
import { PagarmeTokenizationError, tokenizeCard } from '@cannect/utils/pagarme'
import { formatToBRL } from '@cannect/utils/formatters'
import { useInstantConsultation } from 'hooks/useInstantConsultation'

const CHECK_PAYMENT_INTERVAL = 5000
/** Valor base da consulta imediata (em reais). */
const BASE_AMOUNT = 99

const formatCardNumberInput = (value: string): string => {
  const digits = (value || '').replace(/\D/g, '').slice(0, 19)
  return digits.replace(/(\d{4})(?=\d)/g, '$1 ').trim()
}

const formatExpiryInput = (value: string): string => {
  const digits = value.replace(/\D/g, '')
  if (digits.length === 0) return ''
  if (digits.length === 1) return digits
  if (digits.length === 2) return `${digits}/`
  const mm = digits.slice(0, 2)
  const yyyy = digits.slice(2, 6)
  return `${mm}/${yyyy}`
}

const formatCvvInput = (value: string): string => (value || '').replace(/\D/g, '').slice(0, 4)

const splitExpiry = (raw: string): { month: string; year: string } => {
  const [month = '', year = ''] = (raw || '').split('/')
  return { month: month.trim(), year: year.trim() }
}

type PaymentMethod = 'credit_card' | 'pix'

interface CreditCardForm {
  card_holder_name: string
  card_number: string
  card_validate: string
  card_cvv: string
  installments: number
}

interface AppliedCoupon {
  code: string
  couponId: number | null
  /** Valor do desconto em reais. */
  discount: number
  /** Valor final após o desconto, em reais. */
  amount: number
}

export function PaymentStep() {
  const { session, handleEnterQueue, handleCreateSession, loading, goTo, questionnaireCompleted } =
    useInstantConsultation()
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('credit_card')
  const [qrCode, setQrCode] = useState<string | null>(null)
  const [paying, setPaying] = useState(false)
  const [awaitingConfirmation, setAwaitingConfirmation] = useState(false)
  const [paymentError, setPaymentError] = useState<string | null>(null)

  const [couponInput, setCouponInput] = useState('')
  const [couponLoading, setCouponLoading] = useState(false)
  const [appliedCoupon, setAppliedCoupon] = useState<AppliedCoupon | null>(null)

  const checkIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const amountDiscount = appliedCoupon?.discount ?? 0
  const finalAmount = appliedCoupon ? appliedCoupon.amount : BASE_AMOUNT
  const isFree = finalAmount <= 0

  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm<CreditCardForm>({ defaultValues: { installments: 1 } })

  const cardNumberField = register('card_number', { required: 'Campo obrigatório' })
  const cardValidateField = register('card_validate', { required: 'Campo obrigatório' })
  const cardCvvField = register('card_cvv', { required: 'Campo obrigatório' })

  // Recupera/garante a sessão ao montar (ex.: refresh na página de pagamento).
  useEffect(() => {
    if (session) return
    handleCreateSession().catch(() => {
      toast.error('Erro ao preparar sessão. Tente novamente.')
      goTo(questionnaireCompleted ? 'questionnaire' : 'triage')
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const stopPolling = useCallback(() => {
    if (checkIntervalRef.current) {
      clearInterval(checkIntervalRef.current)
      checkIntervalRef.current = null
    }
  }, [])

  useEffect(() => stopPolling, [stopPolling])

  const startPaymentPolling = useCallback(() => {
    if (!session?.id) return
    stopPolling()

    const poll = async () => {
      try {
        const res = await getPaymentStatus(session.id)
        if (res?.status === 'paid') {
          stopPolling()
          setAwaitingConfirmation(false)
          await handleEnterQueue()
        } else if (res?.status === 'refused' || res?.status === 'failed') {
          stopPolling()
          setAwaitingConfirmation(false)
          setQrCode(null)
          setPaymentError('Pagamento recusado. Tente outro método ou cartão.')
        }
      } catch {
        // noop — mantém o polling ativo entre tentativas
      }
    }

    checkIntervalRef.current = setInterval(poll, CHECK_PAYMENT_INTERVAL)
  }, [session?.id, handleEnterQueue, stopPolling])

  // ── Cupom ────────────────────────────────────────────────────────────────
  const handleApplyCoupon = async () => {
    const code = couponInput.trim()
    if (!code || !session) return

    setCouponLoading(true)
    try {
      const res = await validateCoupon({ id: session.id, coupon: code, value: BASE_AMOUNT })
      if (res.success) {
        setAppliedCoupon({
          code,
          couponId: res.coupon_id ?? null,
          discount: res.discount ?? 0,
          amount: res.amount ?? BASE_AMOUNT
        })
        toast.success(res.message || 'Cupom aplicado com sucesso!')
      } else {
        setAppliedCoupon(null)
        toast.error(res.message || 'Cupom inválido.')
      }
    } catch (error: any) {
      setAppliedCoupon(null)
      toast.error(error?.response?.data?.message || 'Não foi possível validar o cupom.')
    } finally {
      setCouponLoading(false)
    }
  }

  const handleRemoveCoupon = () => {
    setAppliedCoupon(null)
    setCouponInput('')
  }

  // ── Pagamento ──────────────────────────────────────────────────────────────
  /** Campos comuns do payload v1 (nunca inclui dados crus do cartão). */
  const buildBasePayload = useCallback(
    (method: TSessionPaymentPayload['payment_method']): TSessionPaymentPayload => ({
      uuid: session!.uuid,
      amount: BASE_AMOUNT,
      amount_discount: amountDiscount,
      payment_method: method || 'none',
      coupon_id: appliedCoupon?.couponId ?? null
    }),
    [session, amountDiscount, appliedCoupon]
  )

  const handleCreditCardPayment = async (data: CreditCardForm) => {
    if (!session) return

    setPaying(true)
    setPaymentError(null)

    // 1) Tokeniza o cartão na Pagar.me (PCI-safe). PAN/CVV nunca vão ao backend.
    let cardToken: string
    try {
      const { month, year } = splitExpiry(data.card_validate)
      cardToken = await tokenizeCard({
        number: data.card_number,
        holder_name: data.card_holder_name,
        exp_month: month,
        exp_year: year,
        cvv: data.card_cvv
      })
    } catch (error) {
      setPaying(false)
      setPaymentError(
        error instanceof PagarmeTokenizationError
          ? error.message
          : 'Não foi possível validar os dados do cartão. Tente novamente.'
      )
      return
    }

    // 2) Paga com o card_token.
    try {
      const payload: TSessionPaymentPayload = {
        ...buildBasePayload('credit_card'),
        card_token: cardToken,
        installments: Number(data.installments) || 1
      }
      const res = await payInstantSession(payload)

      if (res.status === 'paid') {
        await handleEnterQueue()
      } else if (res.status === 'refused' || res.status === 'failed') {
        setPaymentError('Pagamento recusado. Verifique os dados do cartão ou tente outro método.')
      } else {
        // Confirmação assíncrona — acompanha por polling.
        setAwaitingConfirmation(true)
        startPaymentPolling()
      }
    } catch (error: any) {
      // Recusa do adquirente: backend retorna { success:false, message:'<adquirente>' } (400).
      const acquirerMessage = error?.response?.data?.message
      setPaymentError(
        acquirerMessage ? `Pagamento recusado: ${acquirerMessage}` : 'Erro ao processar pagamento. Tente novamente.'
      )
    } finally {
      setPaying(false)
    }
  }

  const handlePixPayment = async () => {
    if (!session) return

    setPaying(true)
    setPaymentError(null)
    try {
      const res = await payInstantSession(buildBasePayload('pix'))
      if (res.qr_code) {
        setQrCode(res.qr_code)
        startPaymentPolling()
      } else if (res.status === 'paid') {
        await handleEnterQueue()
      } else if (res.status === 'refused' || res.status === 'failed') {
        setPaymentError('Não foi possível gerar o PIX. Tente novamente.')
      } else {
        setPaymentError('Não foi possível iniciar o pagamento via PIX. Tente novamente.')
      }
    } catch (error: any) {
      setPaymentError(error?.response?.data?.message || 'Erro ao processar pagamento via PIX.')
    } finally {
      setPaying(false)
    }
  }

  /** Consulta gratuita (cupom cobre 100%): backend confirma automaticamente. */
  const handleFreePayment = async () => {
    if (!session) return
    setPaying(true)
    setPaymentError(null)
    try {
      const res = await payInstantSession({ ...buildBasePayload(null), amount_discount: BASE_AMOUNT })
      if (res.status === 'paid' || res.success) {
        await handleEnterQueue()
      } else {
        setPaymentError('Não foi possível confirmar a consulta gratuita. Tente novamente.')
      }
    } catch (error: any) {
      setPaymentError(error?.response?.data?.message || 'Erro ao confirmar a consulta gratuita.')
    } finally {
      setPaying(false)
    }
  }

  const busy = paying || loading

  const priceSummary = useMemo(
    () => (
      <div className="mb-4 flex flex-col gap-1 rounded-lg bg-gray-50 p-4">
        <div className="flex items-center justify-between text-sm text-gray-600">
          <span>Consulta imediata</span>
          <span>{formatToBRL(BASE_AMOUNT)}</span>
        </div>
        {appliedCoupon && amountDiscount > 0 && (
          <div className="flex items-center justify-between text-sm text-green-600">
            <span>Desconto ({appliedCoupon.code})</span>
            <span>- {formatToBRL(amountDiscount)}</span>
          </div>
        )}
        <div className="mt-1 flex items-center justify-between border-t border-gray-200 pt-2 text-base font-semibold text-gray-800">
          <span>Total</span>
          <span>{formatToBRL(finalAmount)}</span>
        </div>
      </div>
    ),
    [appliedCoupon, amountDiscount, finalAmount]
  )

  return (
    <div className="mx-auto w-full max-w-xl px-4 py-8">
      <div className="mb-8 text-center">
        <h1 className="font-spartan text-2xl font-bold text-gray-800">Pagamento</h1>
        <p className="mt-1 text-sm text-gray-500">Selecione a forma de pagamento para iniciar a consulta</p>
      </div>

      {/* Seleção de método (oculto quando o cupom cobre 100%) */}
      {!isFree && (
        <div className="mb-6 flex gap-3">
          {/* Fundo do selecionado via var suave da marca (inline): a classe
              bg-primary-50 seria pintada com a cor CHEIA pelo theme css e o
              texto sumia (laranja sobre laranja). */}
          <button
            type="button"
            onClick={() => {
              setPaymentMethod('credit_card')
              setQrCode(null)
              setPaymentError(null)
            }}
            style={
              paymentMethod === 'credit_card'
                ? { backgroundColor: 'var(--app-green-soft, rgba(33, 151, 110, 0.12))' }
                : undefined
            }
            className={`flex flex-1 items-center justify-center gap-2 rounded-lg border border-solid py-3 text-sm font-medium transition-all ${
              paymentMethod === 'credit_card'
                ? 'border-primary-700 text-primary-700'
                : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
            }`}>
            <CreditCard size={18} />
            Cartão de crédito
          </button>

          <button
            type="button"
            onClick={() => {
              setPaymentMethod('pix')
              setQrCode(null)
              setPaymentError(null)
            }}
            style={
              paymentMethod === 'pix'
                ? { backgroundColor: 'var(--app-green-soft, rgba(33, 151, 110, 0.12))' }
                : undefined
            }
            className={`flex flex-1 items-center justify-center gap-2 rounded-lg border border-solid py-3 text-sm font-medium transition-all ${
              paymentMethod === 'pix'
                ? 'border-primary-700 text-primary-700'
                : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
            }`}>
            <Smartphone size={18} />
            Pix
          </button>
        </div>
      )}

      <div className="rounded-2xl bg-white p-6 shadow-sm">
        {priceSummary}

        {/* Cupom de desconto */}
        <div className="mb-6">
          {appliedCoupon ? (
            <div className="flex items-center justify-between rounded-lg border border-solid border-green-200 bg-green-50 px-4 py-3">
              <span className="flex items-center gap-2 text-sm font-medium text-green-700">
                <Tag size={16} />
                Cupom {appliedCoupon.code} aplicado
              </span>
              <button
                type="button"
                onClick={handleRemoveCoupon}
                className="flex items-center gap-1 border-none bg-transparent text-xs text-green-700 hover:text-green-900">
                <X size={14} /> remover
              </button>
            </div>
          ) : (
            <div className="flex gap-2">
              <input
                type="text"
                value={couponInput}
                onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
                placeholder="Cupom de desconto"
                className="flex-1 rounded-lg border border-solid border-gray-200 px-4 py-2.5 text-sm uppercase outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
              />
              <button
                type="button"
                onClick={handleApplyCoupon}
                disabled={couponLoading || !couponInput.trim() || !session}
                className="flex items-center justify-center gap-2 rounded-lg border border-solid border-primary-700 bg-white px-4 py-2.5 text-sm font-medium text-primary-700 transition-colors hover:bg-gray-50 disabled:opacity-50">
                {couponLoading && <Loader2 className="h-4 w-4 animate-spin" />}
                Aplicar
              </button>
            </div>
          )}
        </div>

        {/* Consulta gratuita (cupom 100%) */}
        {isFree && (
          <div className="flex flex-col gap-4">
            <p className="text-center text-sm text-gray-600">
              Seu cupom cobre o valor total da consulta. Confirme para iniciar.
            </p>
            {paymentError && <p className="rounded-lg bg-red-50 p-3 text-sm text-red-600">{paymentError}</p>}
            <button
              type="button"
              onClick={handleFreePayment}
              disabled={busy}
              className="flex w-full items-center justify-center gap-2 rounded-lg border-none bg-primary-700 py-3.5 text-base font-semibold text-white transition-colors hover:bg-primary-800 disabled:opacity-60">
              {busy && <Loader2 className="h-4 w-4 animate-spin" />}
              Confirmar consulta gratuita
            </button>
          </div>
        )}

        {!isFree && paymentMethod === 'credit_card' && (
          <form onSubmit={handleSubmit(handleCreditCardPayment)} className="flex flex-col gap-4">
            <label htmlFor="instant-pay-card-number" className="flex flex-col gap-1">
              <span className="text-sm font-medium text-gray-700">Número do cartão *</span>
              <input
                id="instant-pay-card-number"
                type="text"
                inputMode="numeric"
                autoComplete="cc-number"
                maxLength={23}
                placeholder="0000 0000 0000 0000"
                {...cardNumberField}
                onChange={(e) => {
                  e.target.value = formatCardNumberInput(e.target.value)
                  cardNumberField.onChange(e)
                }}
                className="rounded-lg border border-solid border-gray-200 px-4 py-3 text-sm outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
              />
              {errors.card_number && <span className="text-xs text-red-500">{errors.card_number.message}</span>}
            </label>

            <label htmlFor="instant-pay-card-holder" className="flex flex-col gap-1">
              <span className="text-sm font-medium text-gray-700">Nome no cartão *</span>
              <input
                id="instant-pay-card-holder"
                type="text"
                placeholder="Nome como no cartão"
                {...register('card_holder_name', { required: 'Campo obrigatório' })}
                className="rounded-lg border border-solid border-gray-200 px-4 py-3 text-sm outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
              />
              {errors.card_holder_name && (
                <span className="text-xs text-red-500">{errors.card_holder_name.message}</span>
              )}
            </label>

            <div className="grid grid-cols-2 gap-3">
              <label htmlFor="instant-pay-card-expiry" className="flex flex-col gap-1">
                <span className="text-sm font-medium text-gray-700">Validade *</span>
                <input
                  id="instant-pay-card-expiry"
                  type="text"
                  inputMode="numeric"
                  autoComplete="cc-exp"
                  placeholder="MM/AAAA"
                  maxLength={7}
                  {...cardValidateField}
                  onChange={(e) => {
                    e.target.value = formatExpiryInput(e.target.value)
                    cardValidateField.onChange(e)
                  }}
                  className="rounded-lg border border-solid border-gray-200 px-4 py-3 text-sm outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                />
                {errors.card_validate && <span className="text-xs text-red-500">{errors.card_validate.message}</span>}
              </label>

              <label htmlFor="instant-pay-card-cvv" className="flex flex-col gap-1">
                <span className="text-sm font-medium text-gray-700">CVV *</span>
                <input
                  id="instant-pay-card-cvv"
                  type="text"
                  inputMode="numeric"
                  autoComplete="cc-csc"
                  placeholder="***"
                  maxLength={4}
                  {...cardCvvField}
                  onChange={(e) => {
                    e.target.value = formatCvvInput(e.target.value)
                    cardCvvField.onChange(e)
                  }}
                  className="rounded-lg border border-solid border-gray-200 px-4 py-3 text-sm outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                />
                {errors.card_cvv && <span className="text-xs text-red-500">{errors.card_cvv.message}</span>}
              </label>
            </div>

            <label htmlFor="instant-pay-installments" className="flex flex-col gap-1">
              <span className="text-sm font-medium text-gray-700">Parcelas</span>
              <select
                id="instant-pay-installments"
                {...register('installments')}
                className="rounded-lg border border-solid border-gray-200 px-4 py-3 text-sm outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500">
                <option value={1}>1x (sem juros)</option>
                <option value={2}>2x (sem juros)</option>
              </select>
            </label>

            {paymentError && <p className="rounded-lg bg-red-50 p-3 text-sm text-red-600">{paymentError}</p>}

            {awaitingConfirmation && (
              <div className="flex items-center justify-center gap-2 text-xs text-gray-500">
                <Loader2 className="h-3 w-3 animate-spin" />
                Aguardando confirmação do pagamento...
              </div>
            )}

            <button
              type="submit"
              disabled={busy || awaitingConfirmation}
              className="flex w-full items-center justify-center gap-2 rounded-lg border-none bg-primary-700 py-3.5 text-base font-semibold text-white transition-colors hover:bg-primary-800 disabled:opacity-60">
              {(busy || awaitingConfirmation) && <Loader2 className="h-4 w-4 animate-spin" />}
              Pagar {formatToBRL(finalAmount)}
            </button>
          </form>
        )}

        {!isFree && paymentMethod === 'pix' && (
          <div className="flex flex-col items-center gap-4">
            {!qrCode ? (
              <>
                <p className="text-center text-sm text-gray-600">
                  Clique no botão abaixo para gerar o código PIX e realizar o pagamento.
                </p>
                {paymentError && <p className="rounded-lg bg-red-50 p-3 text-sm text-red-600">{paymentError}</p>}
                <button
                  type="button"
                  onClick={handlePixPayment}
                  disabled={busy}
                  className="flex w-full items-center justify-center gap-2 rounded-lg border-none bg-primary-700 py-3.5 text-base font-semibold text-white transition-colors hover:bg-primary-800 disabled:opacity-60">
                  {busy && <Loader2 className="h-4 w-4 animate-spin" />}
                  Gerar código PIX
                </button>
              </>
            ) : (
              <>
                <p className="text-sm font-medium text-gray-700">Escaneie para pagar</p>
                <QRCodeCanvas
                  value={qrCode}
                  size={180}
                  bgColor="#f9f9f9"
                  fgColor="#000"
                  level="L"
                  includeMargin={false}
                />
                <div className="w-full rounded-lg bg-gray-50 p-3">
                  <p className="break-all text-center text-xs text-gray-500">{qrCode}</p>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    navigator.clipboard.writeText(qrCode)
                    toast.success('Código copiado!')
                  }}
                  className="w-full rounded-lg border border-solid border-primary-700 bg-white py-2.5 text-sm font-medium text-primary-700 transition-colors hover:bg-gray-50">
                  Copiar código PIX
                </button>
                <div className="flex items-center gap-2 text-xs text-gray-400">
                  <Loader2 className="h-3 w-3 animate-spin" />
                  Aguardando confirmação do pagamento...
                </div>
              </>
            )}
          </div>
        )}
      </div>

      <button
        type="button"
        onClick={() => goTo(questionnaireCompleted ? 'questionnaire' : 'triage')}
        className="mt-4 w-full border-none bg-transparent text-center text-sm text-gray-400 hover:text-gray-600">
        ← Voltar
      </button>
    </div>
  )
}
